/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, concat, of, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { RouteUtils } from './route-utils.service';
import { RouteDataStateService } from './route-data.state.service';
import { ProjectLegacy } from '../app-data/models/Project';
import { AppState } from './app-state.service';

import { getAnalyticsDecoratedUserId } from '../plain-javascript/services/analytics/getAnalyticsDecoratedUserId';
import bowser from 'bowser';
import { isWebGLEnabled } from 'utils/ticket-utils';

import { IAnalyticsUser } from '../plain-javascript/services/analytics/AnalyticsUser.interface';

@Injectable({
	providedIn: 'root',
})
export class AnalyticsStateService {
	/**
	 * BehaviorSubject is required to get a quick access to route project data lasted emited value without a subscription
	 * this is needed to keep getProjectProps method syncronous.
	 */
	private routeProjectData$: BehaviorSubject<ProjectLegacy> = new BehaviorSubject(null);
	private routeProjectId$: BehaviorSubject<string> = new BehaviorSubject(null);

	/** 'yourUserId@https://firebase-app.com/Root' */
	public analyticsUserId$ = new BehaviorSubject<string>(null);

	constructor(
		private routeDataStateService: RouteDataStateService,
		private routeUtils: RouteUtils,
		private appState: AppState
	) {
		this.listenProjectDataChanges();
		this.listenProjectIdChangesFromRoute();
		this.listenToUserId();
	}

	private listenToUserId() {
		this.appState.userId
			.pipe(map((userId) => getAnalyticsDecoratedUserId(userId)))
			.subscribe(this.analyticsUserId$);
	}

	/**
	 * provides an observable that emits once with the data required to identify with analytics
	 * likely only useful to [[AnalyticsService]]
	 */
	public listenForIdentification(): Observable<{ id: string; userObject: IAnalyticsUser }> {
		// - listen to 1 event per app load and update
		// - if that 1 event doesn't have an email, kick off a second listener that emits only
		// when there's an email then completes
		return this.appState.user.pipe(
			filter((user) => !!user),
			take(1),
			switchMap((user) => {
				if (user.email) {
					return of(user);
				} else {
					return concat(
						of(user),
						this.appState.user.pipe(
							filter((innerUser) => !!innerUser?.email),
							take(1)
						)
					);
				}
			}),
			map((user) => {
				if (user.morphedUser) {
					return {
						id: 'suId',
						userObject: {
							email: 'su@touchplan.io',
							firstName: 'internal',
							lastName: 'tooling',
						},
					};
				}

				const ua = bowser.getParser(window.navigator.userAgent);
				const device = (ua.getPlatform() && ua.getPlatform().model) || ua.getPlatformType() || 'Other';
				const userObject = {
					id: user.id,
					email: user.email,
					firstName: user.firstName,
					lastName: user.lastName,
					createdAt: user.userCreationDate,
					validated_date: user.userValidatedDate,
					phone: user.phone,
					zip: user.zip,
					companyName: user.company,
					method: user.method,
					title: user.title,
					lastDevice: device,
					hasWebGL: isWebGLEnabled(),
				};
				return {
					id: user.$id,
					userObject,
				};
			})
		);
	}

	private listenProjectDataChanges() {
		this.routeDataStateService.projectData$.subscribe(this.routeProjectData$);
	}

	private listenProjectIdChangesFromRoute() {
		this.routeUtils.allRouteParam$
			.pipe(map((route) => (!!route.paramMap.has('projectId') ? route.paramMap.get('projectId') : null)))
			.subscribe(this.routeProjectId$);
	}

	// Receive an array of project property names that Segment is interested in,
	// get the value from the current project object and return.
	public getProjectProps(properties: string[]): { [key: string]: string } {
		// getValue method is prefered to keep this method syncronous.
		const projectData = this.routeProjectData$.getValue();
		const routeProjectId = this.routeProjectId$.getValue();
		return properties.reduce((propertiesObject, propertyName) => {
			// Specific check for setting projectId early, before the projectData may be set.
			if (propertyName === 'projectId' && routeProjectId) {
				propertiesObject[propertyName] = routeProjectId;
			} else if (projectData && projectData[propertyName]) {
				propertiesObject[propertyName] = projectData[propertyName];
			}
			return propertiesObject;
		}, {} as ProjectLegacy);
	}
}
